import axios from 'axios';
import { reportClientError } from './errorReporting';
import { toast } from 'sonner';
import { trackEvent, identifyUser } from './analytics';
import { trackMetaEvent, metaHeaders } from './metaPixel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = 'moneytrack_token';

// Get/set token from localStorage
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token) => localStorage.setItem(TOKEN_KEY, token);
export const removeToken = () => localStorage.removeItem(TOKEN_KEY);

const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT to every request
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Redirect to login on 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      removeToken();
      window.dispatchEvent(new Event('auth:logout'));
    }
    // Server errors, and requests that never got an answer while online.
    // 4xx are expected outcomes (validation, paywall…) and are logged by the
    // backend itself when they matter.
    const status = error.response?.status;
    if ((status >= 500 || (!error.response && error.code !== 'ERR_CANCELED' && navigator.onLine))) {
      reportClientError({
        kind: status ? 'api' : 'network',
        message: status ? `HTTP ${status}` : (error.code || 'sin respuesta'),
        path: error.config?.url || '',
        method: error.config?.method || '',
        status: status || null,
      });
    }
    if (error.response?.status === 402) {
      toast.error(error.response.data?.detail || 'Esta función es parte de MiPisto Pro', {
        description: 'Ve a Configuración para actualizar tu plan.',
      });
    }
    return Promise.reject(error);
  }
);

// Auth
export const register = (data) => api.post('/auth/register', data, { headers: metaHeaders() }).then(r => {
  trackEvent('signup');
  identifyUser(r.data.user);
  // Only after the account actually exists. Same event_id as the CAPI event
  // the backend sends from /auth/register.
  trackMetaEvent('CompleteRegistration', {}, `reg_${r.data.user.user_id}`);
  return r.data;
});
export const login = (data) => api.post('/auth/login', data).then(r => {
  trackEvent('login');
  identifyUser(r.data.user);
  return r.data;
});
export const getMe = () => api.get('/auth/me').then(r => {
  identifyUser(r.data);
  return r.data;
});
export const forgotPassword = (email) => api.post('/auth/forgot-password', { email }).then(r => r.data);
export const resetPassword = (data) => api.post('/auth/reset-password', data).then(r => r.data);

// Leads (marketing capture) — fully independent of auth/registration. Used by
// the landing page's own capture forms and by the signup-abandon recovery
// prompt below; never creates a user, never touches the auth flow.
export const captureLead = (data) => api.post('/leads', data).then(r => r.data);
export const logout = () => {
  removeToken();
  window.dispatchEvent(new Event('auth:logout'));
};

// Profile
export const getProfile = () => api.get('/profile').then(r => r.data);
export const createProfile = (data) => api.post('/profile', data, { headers: metaHeaders() }).then(r => {
  trackEvent('onboarding_completed');
  // The 7-day Pro trial starts when the profile is created (trial_ends_at
  // in server.py's Profile model), not with a separate button.
  if (r.data?.user_id) trackMetaEvent('StartTrial', {}, `trial_${r.data.user_id}`);
  if (data.acquisition_source) trackEvent('acquisition_source_selected', { source: data.acquisition_source });
  return r.data;
});
export const updateProfile = (data) => api.put('/profile', data).then(r => {
  if (data.acquisition_source) trackEvent('acquisition_source_selected', { source: data.acquisition_source });
  return r.data;
});
export const uploadProfilePhoto = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/profile/photo', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }).then(r => r.data);
};

// Categories
export const getCategories = (params) => api.get('/categories', { params }).then(r => r.data);
export const createCategory = (data) => api.post('/categories', data).then(r => r.data);
export const updateCategory = (id, data) => api.put(`/categories/${id}`, data).then(r => r.data);
export const deleteCategory = (id) => api.delete(`/categories/${id}`).then(r => r.data);

// Transactions
export const getTransactions = (params) => api.get('/transactions', { params }).then(r => r.data);
export const createTransaction = (data) => api.post('/transactions', data).then(r => {
  trackEvent('transaction_created', { type: data.type, source: 'manual' });
  return r.data;
});
export const updateTransaction = (id, data) => api.put(`/transactions/${id}`, data).then(r => r.data);
export const deleteTransaction = (id) => api.delete(`/transactions/${id}`).then(r => r.data);
export const dismissPossibleDuplicate = (id) => api.post(`/transactions/${id}/not-duplicate`).then(r => r.data);

// Dashboard
export const getDashboard = (params) => api.get('/dashboard', { params }).then(r => r.data);

// Charts
export const getCharts = (params) => api.get('/charts', { params }).then(r => r.data);

// Voice parsing
export const parseVoice = (text) => api.post('/parse-voice', { text }).then(r => r.data);
export const parseReceipt = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/parse-receipt', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

// Push notifications
export const getVapidPublicKey = () => api.get('/push/vapid-public-key').then(r => r.data);
export const savePushSubscription = (subscription) => api.post('/push/subscribe', subscription).then(r => r.data);
// What this device really has (permission + live subscription), for /admin.
export const reportPushDeviceState = (data) => api.post('/push/device-state', data).then(r => r.data);
export const deletePushSubscription = (endpoint) => api.post('/push/unsubscribe', { endpoint }).then(r => r.data);
export const sendTestPush = () => api.post('/push/test').then(r => r.data);

// Email integrations (Gmail transaction detection)
export const getIntegrations = () => api.get('/integrations').then(r => r.data);
export const connectGoogle = () => api.get('/integrations/google/connect').then(r => r.data);
export const requestGmailAccess = () => api.post('/integrations/google/request-access').then(r => r.data);
export const ackGmailAccessGranted = () => api.post('/integrations/google/access-granted-ack').then(r => r.data);
export const syncGoogle = () => api.post('/integrations/google/sync').then(r => r.data);
export const disconnectGoogle = () => api.delete('/integrations/google').then(r => r.data);

// Chat coach
export const getChatMessages = () => api.get('/chat/messages').then(r => r.data);
export const sendChatMessage = (content) => api.post('/chat/messages', { content }).then(r => r.data);
export const clearChatMessages = () => api.delete('/chat/messages').then(r => r.data);

// Billing (Pro plan)
export const getBillingStatus = () => api.get('/billing/status').then(r => r.data);
// Monthly Pro through a Wompi recurring link (Fase 8): Settings calls this when
// the user is back from Wompi so Pro turns on without waiting for the poll.
export const refreshSubscription = () => api.post('/billing/subscription/refresh').then(r => r.data);
export const createCheckout = (plan = 'monthly') => api.post('/billing/checkout', null, { params: { plan }, headers: metaHeaders() }).then(r => {
  // Fired once the Wompi link exists, right before redirecting to it. Every
  // attempt creates its own link/reference, so each one is its own checkout.
  if (r.data?.meta_event_id) {
    trackMetaEvent('InitiateCheckout', { value: r.data.value, currency: r.data.currency, num_items: 1 }, r.data.meta_event_id);
  }
  return r.data;
});
// 402 = gated feature, no active trial/plan (see has_pro_access in server.py).
// Callers use this instead of a plain toast so the paywall modal (see
// PaywallContext) can take over with an actual upgrade CTA.
export const isPaywallError = (e) => e?.response?.status === 402;

// Goals (Metas) — see server.py's GOALS section.
export const getGoals = () => api.get('/goals').then(r => r.data);
export const getGoal = (id) => api.get(`/goals/${id}`).then(r => r.data);
export const createGoal = (data) => api.post('/goals', data).then(r => r.data);
export const updateGoal = (id, data) => api.put(`/goals/${id}`, data).then(r => r.data);
export const deleteGoal = (id) => api.delete(`/goals/${id}`).then(r => r.data);
export const leaveGoal = (id) => api.post(`/goals/${id}/leave`).then(r => r.data);
export const contributeToGoal = (id, data) => api.post(`/goals/${id}/contributions`, data).then(r => r.data);
export const getGoalInvite = (code) => api.get(`/goals/invite/${code}`).then(r => r.data);
export const joinGoalInvite = (code) => api.post(`/goals/invite/${code}/join`).then(r => r.data);
export const nudgeGoalMember = (id, userId) => api.post(`/goals/${id}/nudge`, { user_id: userId }).then(r => r.data);

// Referrals — mutual free days, see server.py's REFERRALS section.
export const getReferralInfo = () => api.get('/referrals/me').then(r => r.data);

// Account data (privacy): export everything / delete the account.
export const exportAccountData = () => api.get('/account/export').then(r => r.data);
export const deleteAccount = (password) => api.delete('/account', { data: { password } }).then(r => r.data);

// Records that the user explicitly dismissed the mandatory trial-urgency
// popup (see PaywallContext's mustAcknowledge) — visible in /admin/users.
// action: 'upgrade' | 'dismiss' | 'referral' — which of the three ways out they took.
export const acknowledgeTrialUrgency = (action = 'dismiss') => api.post('/billing/acknowledge-urgency', { action }).then(r => r.data);
// Called once per actual display of the popup (not per dismissal) so the
// lifetime cap (TRIAL_URGENCY_MAX_SHOWS) counts someone who force-closes the
// tab too, not just people who click through.
export const recordUrgencyShown = () => api.post('/billing/record-urgency-shown').then(r => r.data);

// One-time in-app feedback prompt (see FeedbackModal.js) — submit (rating
// 1-5 + optional text) or dismiss ("Ahora no, gracias") both permanently
// clear it server-side so it's never shown to the same user twice.
export const submitFeedback = (rating, text = '') => api.post('/feedback', { action: 'submit', rating, text }).then(r => r.data);
export const dismissFeedback = () => api.post('/feedback', { action: 'dismiss' }).then(r => r.data);

// Admin panel (backend/admin.py) — every call 403s unless the logged-in
// account's email is in the server's ADMIN_EMAILS allowlist. Same axios
// instance/JWT interceptor as everything above; nothing admin-specific here.
export const getAdminOverview = () => api.get('/admin/overview').then(r => r.data);
export const getAdminUsers = (params) => api.get('/admin/users', { params }).then(r => r.data);
export const getAdminTransactions = (params) => api.get('/admin/transactions', { params }).then(r => r.data);
export const getAdminPayments = (params) => api.get('/admin/payments', { params }).then(r => r.data);
export const getAdminDeliveryHealth = () => api.get('/admin/delivery-health').then(r => r.data);
export const verifyAdminGmail = () => api.post('/admin/gmail/verify').then(r => r.data);
export const confirmAdminPayment = (reference) => api.post(`/admin/payments/${reference}/confirm`).then(r => r.data);
export const getAdminWompiSubscriptions = (params) => api.get('/admin/wompi-subscriptions', { params }).then(r => r.data);
export const assignAdminWompiSubscription = (id, email) => api.post(`/admin/wompi-subscriptions/${id}/assign`, null, { params: { email } }).then(r => r.data);
export const sendAdminProWelcome = (reference) => api.post(`/admin/payments/${reference}/send-welcome`).then(r => r.data);
export const getAdminLeads = (params) => api.get('/admin/leads', { params }).then(r => r.data);
export const enableAdminGmail = (userId) => api.post(`/admin/users/${userId}/gmail-access`).then(r => r.data);
export const disableAdminGmail = (userId) => api.delete(`/admin/users/${userId}/gmail-access`).then(r => r.data);
export const grantAdminPro = (userId, days) => api.post(`/admin/users/${userId}/grant-pro`, null, { params: { days } }).then(r => r.data);
export const announceToLeads = (dryRun) => api.post('/admin/leads/announce', null, { params: { dry_run: dryRun } }).then(r => r.data);
export const getAdminFeedback = (params) => api.get('/admin/feedback', { params }).then(r => r.data);
export const getAdminNotificationLogs = (params) => api.get('/admin/logs/notifications', { params }).then(r => r.data);
export const getAdminEmailLogs = (params) => api.get('/admin/logs/emails', { params }).then(r => r.data);
export const getAdminErrorLogs = (params) => api.get('/admin/logs/errors', { params }).then(r => r.data);
export const getAdminErrorsSummary = () => api.get('/admin/errors/summary').then(r => r.data);

export default api;
